import React from 'react';
import MagicForm from '../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../Enums/MagicField.enum';
import { User } from '../../../../../graphql/graphql';
import { useEffectAsync } from '../../../../../helpers';
import Loading from '../../../../../Components/Loading';
import usePatientAddressContainer from '../../../Hooks/usePatientAddressContainer';
import { getUsersPESEL } from '../../../../User/Services/userMeta.service';
import { Moment } from 'moment';
import { SelectValue } from '../../../../../Components/Base/Select/Select';

interface Props {
	onSubmit: (values: any) => void;
	user: User;
}

const formScheme: ItemScheme[] = [
	{
		type: MagicField.TEXT_FIELD,
		key: 'salutationBefore',
		title: 'component.patientProfileEdit.salutationBefore',
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'firstName',
		title: 'component.patientProfileEdit.firstName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'lastName',
		title: 'component.patientProfileEdit.lastName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'salutationAfter',
		title: 'component.patientProfileEdit.salutationAfter',
	},
	{
		type: MagicField.CONDITION,
		subType: 'view',
		value: 'citizenship',
		settings: {
			rules: [
				{
					formControlKey: 'citizenship',
					rule: 'notEqual',
					value: '-1',
				},
			],
		},
		children: [
			{
				type: MagicField.TEXT_FIELD,
				key: 'pesel',
				title: 'component.patientProfileEdit.pesel',
				settings: {
					required: true,
				},
			},
		],
	},
	{
		type: MagicField.CONDITION,
		subType: 'view',
		value: 'citizenship',
		settings: {
			rules: [
				{
					formControlKey: 'citizenship',
					rule: 'equal',
					value: '-1',
				},
			],
		},
		children: [
			{
				type: MagicField.TEXT_FIELD,
				key: 'pesel',
				title: 'component.patientProfileEdit.pesel',
				settings: {
					required: false,
				},
			},
		],
	},
	{
		type: MagicField.DATEPICKER,
		key: 'birthDate',
		title: 'component.patientProfileEdit.birthDate',
		settings: {
			required: true,
			rules: [
				{
					rule: 'max',
					value: 'today',
				},
			],
		},
	},
	{
		type: MagicField.EMAIL,
		key: 'email',
		title: 'component.patientProfileEdit.email',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.PHONE_NUMBER,
		key: 'phoneNumber',
		title: 'component.patientProfileEdit.phone',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.CITIZENSHIP_SELECT,
		key: 'citizenship',
		title: 'component.patientProfileEdit.citizenship',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.HEALTH_INSURANCE_COMPANY_SELECT,
		key: 'healthInsuranceCompany',
		title: 'component.patientProfileEdit.healthInsuranceCompany',
	},
	{
		type: MagicField.GENDER,
		key: 'gender',
		title: 'component.patientProfileEdit.gender',
	},
];

const PatientProfileEditPoland: React.FC<Props> = ({ onSubmit, user }) => {
	const { addAddressContainerToScheme, isLoading } =
		usePatientAddressContainer();

	const defaultValues: Record<string, any> = {
		salutationBefore: user.profile?.salutationBefore,
		salutationAfter: user.profile?.salutationAfter,
		firstName: user.profile?.firstName,
		lastName: user.profile?.lastName,
		birthDate: user.profile?.birthDate,
		address: user.profile?.address,
		email: user?.profile?.contactEmail ?? user?.email,
		phoneNumber: user?.phoneNumber,
		citizenship: user?.profile?.nationalityTermItem?.id,
		healthInsuranceCompany: user?.profile?.insuranceCompanyTermItem?.id,
		pesel: getUsersPESEL(user),
		gender: user?.profile?.gender,
	};
	const handleSubmit = (values: {
		salutationBefore?: string;
		salutationAfter?: string;
		firstName: string;
		lastName: string;
		birthDate: Moment;
		address: object;
		email: string;
		phoneNumber: string;
		citizenship: SelectValue;
		healthInsuranceCompany?: SelectValue;
		pesel?: string;
		gender?: SelectValue;
	}) => {
		const data = {
			salutationBefore: values.salutationBefore,
			salutationAfter: values.salutationAfter,
			firstName: values.firstName,
			lastName: values.lastName,
			birthDate: values.birthDate,
			address: values.address,
			email: values.email,
			phoneNumber: values.phoneNumber,
			citizenship: values.citizenship.value,
			insuranceCompanyTermItemId: values.healthInsuranceCompany?.value,
			pesel: values.pesel,
			gender: values.gender?.value,
		};
		onSubmit(data);
	};

	useEffectAsync(async () => {
		if (!formScheme.some(item => item.key === 'address')) {
			await addAddressContainerToScheme(formScheme);
		}
	});

	if (isLoading) {
		return <Loading />;
	}

	return (
		<MagicForm
			formScheme={formScheme}
			onSubmit={handleSubmit}
			defaultValues={defaultValues}
			showCharCounter={false}
		/>
	);
};

export default PatientProfileEditPoland;
